import {CountryCode} from 'libphonenumber-js';

import {useMemo} from 'react';

import {defaultTo} from 'ramda';

import {useGetCountriesQuery} from '@dms/api/core';
import {CountryResponseBody} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {isPriorityCountry, PRIORITY_COUNTRIES} from './utils/isPriorityCountry';

type Code = CountryCode | string | Nullish;

export function useCountry() {
  const {data} = useGetCountriesQuery();

  const countryByCode = useMemo(() => {
    const map: Record<string, CountryResponseBody> = {};
    (data ?? []).forEach((item) => {
      map[item.code] = item;
    });
    return map;
  }, [data]);
  const countriesOptions = useMemo(() => (data ?? []).map(countryToOption), [data]);

  const sortedCountries = useMemo(() => sortCountriesWithPriority(data ?? []), [data]);
  const sortedCountriesOptions = useMemo(
    () => sortedCountries.map(countryToOption),
    [sortedCountries]
  );

  return {
    countriesOptions,
    sortedCountriesOptions,
    getCountry: (code: Code) => defaultTo(null, countryByCode[code ?? '']),
    getCountryName: (code: Code) => defaultTo(null, countryByCode[code ?? '']?.name),
    getPrimaryCurrency: (code: Code) => defaultTo(null, countryByCode[code ?? '']?.primaryCurrency),
    getSortedCountries: () => sortedCountries,
  };
}

// Countries with priority are sorted to the top of the list
const sortCountriesWithPriority = (countries: CountryResponseBody[]) => {
  if (!countries || countries.length === 0) {
    return [];
  }

  const priorityCountries: CountryResponseBody[] = [];
  const otherCountries: CountryResponseBody[] = [];

  countries.forEach((country) => {
    if (isPriorityCountry(country.code)) {
      const priorityIndex = PRIORITY_COUNTRIES.indexOf(country.code);
      // eslint-disable-next-line security/detect-object-injection
      priorityCountries[priorityIndex] = country;
    } else {
      otherCountries.push(country);
    }
  });

  return [...priorityCountries.filter(Boolean), ...otherCountries];
};

const countryToOption = (country: CountryResponseBody) => ({
  value: country.code,
  label: country.name,
});
